import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { FaStar, FaStarHalfAlt, FaQuoteLeft, FaChevronLeft, FaChevronRight, FaCheckCircle } from 'react-icons/fa';
import '../../styles/testimonials.css';

// Static testimonials data
const testimonialsData = [
  {
    id: 1,
    name: "Riya Sharma",
    course: "Full Stack Development",
    rating: 5,
    text: "The teaching methodology is excellent! I got placed in Infosys within 2 months of completing the course. The instructors are very supportive and the projects were real-world based. Highly recommended for anyone looking to start their IT career!",
    designation: "Software Engineer at Infosys",
    verified: true,
    date: "March 2024"
  },
  {
    id: 2,
    name: "Amit Kumar",
    course: "Python Programming",
    rating: 5,
    text: "Best decision I ever made! The practical approach and regular assignments helped me master Python. The placement team's support was outstanding. They helped me prepare for interviews and build a strong resume.",
    designation: "Data Analyst at TCS",
    verified: true,
    date: "February 2024"
  },
  {
    id: 3,
    name: "Priya Patel",
    course: "Java Development",
    rating: 5,
    text: "Amazing learning experience! The faculty is knowledgeable and always available to clear doubts. The live projects gave me confidence for interviews. I got multiple job offers after completing this course.",
    designation: "Java Developer at Wipro",
    verified: true,
    date: "January 2024"
  },
  {
    id: 4,
    name: "Rajesh Gupta",
    course: "Web Design",
    rating: 4,
    text: "Great curriculum and hands-on training. I started my own freelancing business after this course. The UI/UX modules were particularly helpful. Highly recommend for beginners who want to learn from scratch!",
    designation: "Freelance Web Designer",
    verified: true,
    date: "December 2023"
  },
  {
    id: 5,
    name: "Neha Singh",
    course: "Data Science",
    rating: 5,
    text: "The course content is up-to-date with industry requirements. The capstone project was challenging and helped me build a strong portfolio. The instructors have deep knowledge and explain complex concepts easily.",
    designation: "Data Scientist at Amazon",
    verified: true,
    date: "March 2024"
  },
  {
    id: 6,
    name: "Vikram Malhotra",
    course: "Android Development",
    rating: 5,
    text: "From zero coding knowledge to building my own app! The step-by-step approach and regular doubt-solving sessions made learning easy. The course structure is well-planned and practical.",
    designation: "Mobile App Developer",
    verified: true,
    date: "February 2024"
  }
];

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleCards, setVisibleCards] = useState(1);
  const [isPlaying, setIsPlaying] = useState(true);
  const intervalRef = useRef(null);
  const sectionRef = useRef(null);

  // Handle responsive visible cards
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1200) {
        setVisibleCards(3);
      } else if (window.innerWidth >= 768) {
        setVisibleCards(2);
      } else {
        setVisibleCards(1);
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Scroll animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1, triggerOnce: true }
    );

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observer.observe(el));

    return () => {
      elements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  // Auto slide
  const startAutoSlide = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = setInterval(() => {
      setCurrentIndex((prevIndex) => {
        const maxIndex = Math.max(0, testimonialsData.length - visibleCards);
        return prevIndex >= maxIndex ? 0 : prevIndex + 1;
      });
    }, 5000);
  }, [visibleCards]);

  useEffect(() => {
    if (isPlaying) {
      startAutoSlide();
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPlaying, startAutoSlide, visibleCards]);

  const handlePrev = () => {
    setIsPlaying(false);
    setCurrentIndex((prevIndex) => {
      const maxIndex = Math.max(0, testimonialsData.length - visibleCards);
      return prevIndex === 0 ? maxIndex : prevIndex - 1;
    });
    setTimeout(() => setIsPlaying(true), 10000);
  };

  const handleNext = () => {
    setIsPlaying(false);
    setCurrentIndex((prevIndex) => {
      const maxIndex = Math.max(0, testimonialsData.length - visibleCards);
      return prevIndex >= maxIndex ? 0 : prevIndex + 1;
    });
    setTimeout(() => setIsPlaying(true), 10000);
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    
    for (let i = 0; i < fullStars; i++) {
      stars.push(<FaStar key={`star-${i}`} className="star-filled" />);
    }
    if (hasHalfStar) {
      stars.push(<FaStarHalfAlt key="star-half" className="star-filled" />);
    }
    const remainingStars = 5 - stars.length;
    for (let i = 0; i < remainingStars; i++) {
      stars.push(<FaStar key={`star-empty-${i}`} className="star-empty" />);
    }
    return stars;
  };

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const visibleTestimonials = testimonialsData.slice(currentIndex, currentIndex + visibleCards);
  const totalSlides = Math.ceil(testimonialsData.length / visibleCards);
  const currentSlide = Math.floor(currentIndex / visibleCards) + 1;

  if (visibleTestimonials.length === 0) {
    return null;
  }

  return (
    <div className="testimonials-section" ref={sectionRef}>
      <Container>
        <div className="testimonials-header">
          <h2 className="testimonials-title">
            What Our <span className="text-primary">Students Say</span>
          </h2>
          <p className="testimonials-subtitle">
            Don't just take our word for it - hear from our successful graduates
          </p>
        </div>

        <div className="testimonials-carousel-container">
          <button 
            className="testimonial-nav-btn prev-btn"
            onClick={handlePrev}
            aria-label="Previous"
          >
            <FaChevronLeft />
          </button>

          <div className="testimonials-track">
            <Row className="g-4 justify-content-center">
              {visibleTestimonials.map((testimonial, idx) => (
                <Col md={12 / visibleCards} key={testimonial.id}>
                  <div className="testimonial-card animate-on-scroll">
                    <div className="testimonial-quote-icon">
                      <FaQuoteLeft />
                    </div>
                    <div className="testimonial-rating mb-3">
                      {renderStars(testimonial.rating)}
                    </div>
                    <p className="testimonial-text">
                      "{testimonial.text}"
                    </p>
                    <div className="testimonial-student">
                      <div className="student-avatar">
                        <div className="avatar-placeholder">
                          {getInitials(testimonial.name)}
                        </div>
                      </div>
                      <div className="student-info">
                        <h5 className="student-name">
                          {testimonial.name}
                          {testimonial.verified && (
                            <FaCheckCircle className="verified-badge" />
                          )}
                        </h5>
                        <p className="student-course">{testimonial.course}</p>
                        <p className="student-designation">{testimonial.designation}</p>
                      </div>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </div>

          <button 
            className="testimonial-nav-btn next-btn"
            onClick={handleNext}
            aria-label="Next"
          >
            <FaChevronRight />
          </button>
        </div>

        <div className="testimonials-dots">
          {Array.from({ length: totalSlides }).map((_, idx) => (
            <button
              key={idx}
              className={`dot ${currentSlide === idx + 1 ? 'active' : ''}`}
              onClick={() => {
                setIsPlaying(false);
                setCurrentIndex(idx * visibleCards);
                setTimeout(() => setIsPlaying(true), 10000);
              }}
              aria-label={`Go to slide ${idx + 1}`}
            />
          ))}
        </div>
      </Container>
    </div>
  );
};

export default Testimonials;